import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Avatar } from 'flowbite-react';
import { RootState, loadProjects, useAppDispatch, useAppSelector } from '../../redux';
import { Project } from '../../models';

const AUTO_SHOW_ALL_THRESHOLD = 3;

function FavoriteProjectsStrip() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const { user } = useAppSelector((state: RootState) => state.auth);
    const { projects } = useAppSelector((state: RootState) => state.projects);

    const favoriteIds = user?.favoriteProjects || [];

    useEffect(() => {
        dispatch(loadProjects());
    }, [dispatch]);

    const stripProjects = useMemo(() => {
        if (!projects?.length)
            return [];

        const accessible = [...projects]
            .filter((p: Project) => p.active !== false)
            .sort((a: Project, b: Project) =>
                a.name.localeCompare(b.name)
            );

        // Few accessible projects → show them all (no need to star favorites).
        if (accessible.length <= AUTO_SHOW_ALL_THRESHOLD)
            return accessible;

        if (!favoriteIds.length)
            return [];

        return accessible.filter((p: Project) => favoriteIds.includes(p._id));
    }, [projects, favoriteIds]);

    if (stripProjects.length === 0)
        return null;

    return (
        <nav
            aria-label={t('projects.favorite') as string}
            className="border-b bg-white px-4 py-3 dark:bg-gray-900 sm:px-6 lg:px-8"
        >
            <ul className="flex gap-2 overflow-x-auto pb-0.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                {stripProjects.map((project: Project) => (
                    <li key={project._id} className="shrink-0">
                        <Link
                            to={Project.path(project)}
                            className="inline-flex max-w-[12rem] items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-sm font-medium text-gray-900 hover:bg-blue-50 dark:hover:bg-blue-950/40 hover:no-underline dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
                        >
                            <Avatar img={project.getAvatarUrl()} size="xs" rounded className="shrink-0" />
                            <span className="truncate">{project.name}</span>
                        </Link>
                    </li>
                ))}
            </ul>
        </nav>
    );
}

export default FavoriteProjectsStrip;
